/**
 * BOQ Detail Component
 * Single BOQ view with sections, entries, and actions — Studio theme
 */

import { toProjectParticipantRole } from "@shared/types";
import { useQueryClient } from "@tanstack/react-query";
import {
	AlertCircle,
	ArrowLeft,
	Edit2,
	FileText,
	Loader2,
	Lock,
	MessageSquare,
	MoreVertical,
	Plus,
	Printer,
	Send,
	Sparkles,
	Trash2,
} from "lucide-react";
import { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import {
	DropdownMenu,
	DropdownMenuContent,
	DropdownMenuItem,
	DropdownMenuSeparator,
	DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Spinner } from "@/components/ui/spinner";
import { useAuthStore } from "@/features/auth/store/auth.store";
import { ShareAction } from "@/features/sharing/components/share-action";
import { FloatPanel, StudioButton } from "@/features/studio/theme";
import { queryKeys } from "@/lib/query-keys";
import {
	type BOQDetails,
	type BOQEntry,
	type BOQSection,
	type BoqStatus,
	canCreateChangeRequest,
	canLockBOQ,
	canReopenBOQ,
	canSendBOQ,
	deleteBOQ,
	deleteBOQEntry,
	deleteBOQSection,
	formatCurrency,
	getBOQById,
	getStatusLabel,
	isBOQEditable,
	lockBOQ,
	reopenBOQ,
	sendBOQ,
} from "../../services/boq/boq-api.service";
import { AddEntryDialog } from "./add-entry-dialog";
import { AddSectionDialog } from "./add-section-dialog";
import { ChangeRequestsList } from "./change-requests-list";
import { EditEntryDialog } from "./edit-entry-dialog";
import { EditSectionDialog } from "./edit-section-dialog";
import { openBoqPrintView } from "./open-boq-print-view";
import { RequestChangesDialog } from "./request-changes-dialog";

// ─── Status chip ─────────────────────────────────────────────────────────────
function StatusChip({ status }: { status: BoqStatus }) {
	const label = getStatusLabel(status);
	const palette: Record<string, { bg: string; color: string; border: string }> =
		{
			draft: {
				bg: "var(--color-dashboard-divider)",
				color: "var(--color-dashboard-muted)",
				border: "var(--color-dashboard-border)",
			},
			sent: {
				bg: "var(--color-dashboard-info-bg-soft)",
				color: "var(--color-dashboard-pill-info)",
				border: "var(--color-dashboard-info-border)",
			},
			approved: {
				bg: "var(--color-dashboard-success-bg)",
				color: "var(--color-dashboard-success)",
				border: "var(--color-dashboard-success-border)",
			},
			locked: {
				bg: "var(--color-dashboard-surface)",
				color: "var(--color-dashboard-muted)",
				border: "var(--color-dashboard-border)",
			},
			change_requested: {
				bg: "var(--color-dashboard-pill-awaiting-bg)",
				color: "var(--color-dashboard-pill-awaiting)",
				border: "var(--color-dashboard-warning-border)",
			},
		};
	const style = palette[status] ?? palette.draft;
	return (
		<span
			style={{
				display: "inline-flex",
				alignItems: "center",
				padding: "3px 10px",
				borderRadius: 14,
				fontSize: 10,
				fontFamily: "var(--font-mono)",
				fontWeight: 600,
				letterSpacing: "0.1em",
				textTransform: "uppercase",
				background: style.bg,
				color: style.color,
				border: `1px solid ${style.border}`,
			}}
		>
			{label}
		</span>
	);
}

// ─── Main component ───────────────────────────────────────────────────────────
export function BOQDetail() {
	const { t } = useTranslation();
	const { boqId } = useParams<{ boqId: string }>();
	const navigate = useNavigate();
	const queryClient = useQueryClient();
	const userRole = useAuthStore((state) => state.getUserType());
	const [boqData, setBoqData] = useState<BOQDetails | null>(null);
	const [loading, setLoading] = useState(true);
	const [actionLoading, setActionLoading] = useState<string | null>(null);
	const [showAddSection, setShowAddSection] = useState(false);
	const [showAddEntry, setShowAddEntry] = useState(false);
	const [selectedSectionId, setSelectedSectionId] = useState<
		string | undefined
	>();
	const [showRequestChanges, setShowRequestChanges] = useState(false);
	const [changeRequestsRefresh, setChangeRequestsRefresh] = useState(0);

	const [editSection, setEditSection] = useState<BOQSection | null>(null);
	const [editEntry, setEditEntry] = useState<BOQEntry | null>(null);
	const [deleteSection, setDeleteSection] = useState<BOQSection | null>(null);
	const [deleteEntry, setDeleteEntry] = useState<BOQEntry | null>(null);
	const [deleteBoqConfirm, setDeleteBoqConfirm] = useState(false);
	const [lockConfirm, setLockConfirm] = useState(false);
	const lockDialogRef = useRef<HTMLDivElement>(null);

	// The lock dialog moves focus to its first button, Cancel, and also closes
	// on Escape pressed anywhere, as RequestChangesDialog does: the overlay's own
	// keydown handler fires only once focus is inside it.
	useEffect(() => {
		if (!lockConfirm) return;
		lockDialogRef.current?.querySelector("button")?.focus();
		const handle = (e: KeyboardEvent) => {
			if (e.key === "Escape") setLockConfirm(false);
		};
		window.addEventListener("keydown", handle);
		return () => window.removeEventListener("keydown", handle);
	}, [lockConfirm]);
	const [isDeleting, setIsDeleting] = useState(false);

	const loadBOQ = useCallback(async () => {
		if (!boqId) return;
		try {
			const result = await getBOQById(boqId);
			if (result.error) {
				setBoqData(null);
			} else {
				setBoqData(result.data);
			}
		} catch {
			setBoqData(null);
		} finally {
			setLoading(false);
		}
	}, [boqId]);

	useEffect(() => {
		loadBOQ();
	}, [loadBOQ]);

	const handleSend = async () => {
		if (!boqId) return;
		setActionLoading("send");
		try {
			const result = await sendBOQ(boqId);
			if (result.success) {
				toast.success(
					result.boq?.status === "approved"
						? "BOQ sent. The homeowner had already approved every line, so it is approved."
						: "BOQ sent to homeowner successfully",
				);
				await loadBOQ();
			} else {
				toast.error(result.error || "Failed to send BOQ");
			}
		} finally {
			setActionLoading(null);
		}
	};

	const handleReopen = async () => {
		if (!boqId) return;
		setActionLoading("reopen");
		try {
			const result = await reopenBOQ(boqId);
			if (result.success) {
				toast.success(
					"BOQ is a draft again. Send it when your edits are done.",
				);
				await loadBOQ();
			} else {
				toast.error(result.error || "Failed to reopen BOQ");
			}
		} finally {
			setActionLoading(null);
		}
	};

	const handleLock = async () => {
		if (!boqId) return;
		setActionLoading("lock");
		try {
			const result = await lockBOQ(boqId);
			if (result.success) {
				toast.success("BOQ locked successfully");
				await loadBOQ();
			} else {
				toast.error(result.error || "Failed to lock BOQ");
			}
		} finally {
			setActionLoading(null);
		}
	};

	const handleDeleteSection = async () => {
		if (!boqData || !deleteSection) return;
		setIsDeleting(true);
		try {
			const result = await deleteBOQSection(boqData.boq.id, deleteSection.id);
			if (result.success) {
				toast.success("Section deleted successfully");
				setDeleteSection(null);
				loadBOQ();
			} else {
				toast.error(result.error || "Failed to delete section");
			}
		} finally {
			setIsDeleting(false);
		}
	};

	const handleDeleteEntry = async () => {
		if (!boqData || !deleteEntry) return;
		setIsDeleting(true);
		try {
			const result = await deleteBOQEntry(
				boqData.boq.id,
				deleteEntry.boqSectionId,
				deleteEntry.id,
			);
			if (result.success) {
				toast.success("Entry deleted successfully");
				setDeleteEntry(null);
				loadBOQ();
			} else {
				toast.error(result.error || "Failed to delete entry");
			}
		} finally {
			setIsDeleting(false);
		}
	};

	const handleDeleteBoq = async () => {
		if (!boqData) return;
		setIsDeleting(true);
		try {
			const result = await deleteBOQ(boqData.boq.id);
			if (result.success) {
				toast.success("BOQ deleted successfully");
				queryClient.invalidateQueries({
					queryKey: queryKeys.projects.boq(boqData.boq.projectId),
				});
				navigate(`/projects/${boqData.boq.projectId}?tab=boq`);
			} else {
				toast.error(result.error || "Failed to delete BOQ");
			}
		} finally {
			setIsDeleting(false);
		}
	};

	// ── Loading state ──────────────────────────────────────────────────────────
	if (loading) {
		return (
			<div
				style={{
					display: "flex",
					flexDirection: "column",
					alignItems: "center",
					justifyContent: "center",
					gap: 12,
					padding: "64px 0",
				}}
			>
				<Spinner label={t("loaders.loadingBoq")} className="size-8" />
				<p
					aria-hidden="true"
					className="text-dashboard-placeholder"
					style={{
						fontFamily: "var(--font-mono)",
						fontSize: 11,
						letterSpacing: "0.1em",
						textTransform: "uppercase",
					}}
				>
					{t("loaders.loadingBoq")}…
				</p>
			</div>
		);
	}

	// ── Not found ──────────────────────────────────────────────────────────────
	if (!boqData) {
		return (
			<FloatPanel elevation={1} padding={48}>
				<div style={{ textAlign: "center" }}>
					<AlertCircle
						style={{
							color: "var(--color-dashboard-accent-dark)",
							margin: "0 auto 16px",
							display: "block",
						}}
						size={40}
					/>
					<p
						style={{
							fontFamily: "var(--font-display)",
							fontStyle: "italic",
							fontSize: 20,
							color: "var(--color-dashboard-text)",
						}}
					>
						BOQ not found
					</p>
					<p
						style={{
							fontFamily: "var(--font-body)",
							fontSize: 13,
							color: "var(--color-dashboard-placeholder)",
							marginTop: 8,
						}}
					>
						The BOQ you're looking for doesn't exist or you don't have access to
						it.
					</p>
				</div>
			</FloatPanel>
		);
	}

	const { boq, sections } = boqData;
	const projectRole = toProjectParticipantRole(userRole) ?? undefined;
	const editable = projectRole ? isBOQEditable(boq, projectRole) : false;
	const canSend = canSendBOQ(boq);
	const canLock = canLockBOQ(boq);
	const canReopen = projectRole ? canReopenBOQ(boq, projectRole) : false;
	// The designer or a hired contractor: who sends, locks and answers requests.
	const isProvider = projectRole === "designer" || projectRole === "contractor";
	const canRequestChanges = projectRole
		? canCreateChangeRequest(boq, projectRole)
		: false;
	const canShareBoq =
		!!projectRole &&
		projectRole !== "homeowner" &&
		(canSend ||
			["sent", "approved", "change_requested", "locked"].includes(boq.status));

	const handleChangeRequestSuccess = () => {
		loadBOQ();
		setChangeRequestsRefresh((prev) => prev + 1);
	};

	// ── Token shortcuts ────────────────────────────────────────────────────────
	const mono = { fontFamily: "var(--font-mono)" } as const;
	const display = {
		fontFamily: "var(--font-display)",
		fontStyle: "italic",
	} as const;
	const body = { fontFamily: "var(--font-body)" } as const;

	return (
		<div style={{ display: "flex", flexDirection: "column", gap: 20 }}>
			{/* ── Header ─────────────────────────────────────────────────────── */}
			<FloatPanel elevation={1} padding={24}>
				<div
					style={{
						display: "flex",
						alignItems: "flex-start",
						justifyContent: "space-between",
						gap: 16,
						flexWrap: "wrap",
					}}
				>
					{/* Left: back + title */}
					<div style={{ display: "flex", alignItems: "flex-start", gap: 14 }}>
						<StudioButton
							variant="ghost"
							size="sm"
							icon={ArrowLeft}
							onClick={() => navigate(`/projects/${boq.projectId}?tab=boq`)}
						/>
						<div>
							<div
								style={{
									display: "flex",
									alignItems: "center",
									gap: 10,
									flexWrap: "wrap",
								}}
							>
								<h1
									style={{
										...display,
										fontSize: 26,
										fontWeight: 500,
										color: "var(--color-dashboard-text)",
										letterSpacing: "-0.02em",
										margin: 0,
									}}
								>
									{boq.title || `BOQ Version ${boq.versionNumber}`}
								</h1>
								<StatusChip status={boq.status} />
							</div>
							<div
								style={{
									display: "flex",
									alignItems: "center",
									gap: 8,
									marginTop: 6,
									flexWrap: "wrap",
								}}
							>
								{[
									`v${boq.versionNumber}`,
									boq.currencyCode,
									`Created by ${boq.createdByRole}`,
									new Date(boq.createdAt).toLocaleDateString(),
								].map((item, i, arr) => (
									<span key={item}>
										<span
											style={{
												...mono,
												fontSize: 10,
												letterSpacing: "0.06em",
												color: "var(--color-dashboard-placeholder)",
												textTransform: "uppercase",
											}}
										>
											{item}
										</span>
										{i < arr.length - 1 && (
											<span
												style={{
													...mono,
													fontSize: 10,
													color: "var(--color-dashboard-faint-text)",
													marginLeft: 8,
												}}
											>
												·
											</span>
										)}
									</span>
								))}
							</div>
						</div>
					</div>

					{/* Right: actions */}
					<div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
						<StudioButton
							variant="secondary"
							size="sm"
							icon={Printer}
							onClick={() => openBoqPrintView(boq.id)}
						>
							Print / Save as PDF
						</StudioButton>
						{canShareBoq && (
							<ShareAction
								artifact={{
									type: "boq",
									id: boq.id,
									projectId: boq.projectId,
								}}
								label="Share"
							/>
						)}
						{editable && (
							<>
								{canSend && (
									<StudioButton
										variant="primary"
										size="sm"
										icon={Send}
										onClick={handleSend}
										disabled={actionLoading === "send"}
									>
										{actionLoading === "send"
											? "Sending…"
											: "Send to Homeowner"}
									</StudioButton>
								)}
								{boq.status === "draft" && (
									<StudioButton
										variant="secondary"
										size="sm"
										icon={Trash2}
										onClick={() => setDeleteBoqConfirm(true)}
									>
										Delete BOQ
									</StudioButton>
								)}
							</>
						)}
						{/* Locking is for a BOQ that has been sent, so it sits outside
						    the editable block, which is drafts and change requests. */}
						{canReopen && (
							<StudioButton
								variant="secondary"
								size="sm"
								icon={Edit2}
								onClick={handleReopen}
								disabled={actionLoading === "reopen"}
							>
								{actionLoading === "reopen" ? "Reopening…" : "Edit BOQ"}
							</StudioButton>
						)}
						{canLock && isProvider && (
							<StudioButton
								variant="primary"
								size="sm"
								icon={Lock}
								onClick={() => setLockConfirm(true)}
								disabled={actionLoading === "lock"}
							>
								{actionLoading === "lock" ? "Locking…" : "Lock BOQ"}
							</StudioButton>
						)}
						{canRequestChanges && (
							<StudioButton
								variant="secondary"
								size="sm"
								icon={MessageSquare}
								onClick={() => setShowRequestChanges(true)}
							>
								Request Changes
							</StudioButton>
						)}
					</div>
				</div>
			</FloatPanel>

			{/* ── AI generation note ─────────────────────────────────────────── */}
			{boqData.generationRun && (
				<FloatPanel elevation={1} padding={20}>
					<div style={{ display: "flex", gap: 14 }}>
						<Sparkles
							size={18}
							style={{
								color: "var(--color-dashboard-accent-dark)",
								flexShrink: 0,
								marginTop: 2,
							}}
						/>
						<div style={{ flex: 1 }}>
							<p
								style={{
									...display,
									fontSize: 14,
									fontWeight: 500,
									color: "var(--color-dashboard-text)",
									margin: 0,
								}}
							>
								{t("boq.starter.draftTitle")}
							</p>
							<p
								style={{
									...body,
									fontSize: 12.5,
									color: "var(--color-dashboard-muted)",
									marginTop: 4,
								}}
							>
								{boqData.generationRun.template?.name
									? t("boq.starter.draftFromTemplate", {
											template: boqData.generationRun.template.name,
										})
									: t("boq.starter.draftFromUnknownTemplate")}
							</p>
							{boqData.generationRun.assumptions?.length > 0 && (
								<div style={{ marginTop: 12 }}>
									<p
										style={{
											...mono,
											fontSize: 9,
											letterSpacing: "0.1em",
											textTransform: "uppercase",
											color: "var(--color-dashboard-muted)",
											marginBottom: 6,
										}}
									>
										{t("boq.starter.assumptionsTitle")}
									</p>
									<ul
										style={{
											margin: 0,
											paddingLeft: 16,
											display: "flex",
											flexDirection: "column",
											gap: 3,
										}}
									>
										{boqData.generationRun.assumptions.map((a) => (
											<li
												key={`${a.label}-${a.source}`}
												style={{
													...body,
													fontSize: 12.5,
													color: "var(--color-dashboard-muted-dark)",
												}}
											>
												{a.label}: {a.value}
											</li>
										))}
									</ul>
								</div>
							)}
							{boqData.generationRun.warnings?.length > 0 && (
								<div
									style={{
										marginTop: 12,
										padding: "10px 14px",
										background: "var(--color-dashboard-warning-bg-soft)",
										border: "1px solid var(--color-dashboard-warning-border)",
										borderRadius: 7,
									}}
								>
									<p
										style={{
											...mono,
											fontSize: 9,
											letterSpacing: "0.1em",
											textTransform: "uppercase",
											color: "var(--color-dashboard-pill-awaiting)",
											marginBottom: 6,
										}}
									>
										{t("boq.starter.warningsTitle")}
									</p>
									<ul
										style={{
											margin: 0,
											paddingLeft: 16,
											display: "flex",
											flexDirection: "column",
											gap: 3,
										}}
									>
										{boqData.generationRun.warnings.map((w) => {
											return (
												<li
													key={w.code}
													style={{
														...body,
														fontSize: 12.5,
														color: "var(--color-dashboard-warning)",
													}}
												>
													{w.message}
												</li>
											);
										})}
									</ul>
								</div>
							)}
						</div>
					</div>
				</FloatPanel>
			)}

			{/* ── Notes ─────────────────────────────────────────────────────── */}
			{boq.notes && (
				<FloatPanel elevation={1} padding={20}>
					<p
						style={{
							...mono,
							fontSize: 9,
							letterSpacing: "0.1em",
							textTransform: "uppercase",
							color: "var(--color-dashboard-muted)",
							marginBottom: 8,
						}}
					>
						{t("boq.detail.notes")}
					</p>
					<p
						style={{
							...body,
							fontSize: 13.5,
							color: "var(--color-dashboard-muted-dark)",
							whiteSpace: "pre-wrap",
							lineHeight: 1.6,
						}}
					>
						{boq.notes}
					</p>
				</FloatPanel>
			)}

			{/* ── Sections ──────────────────────────────────────────────────── */}
			<div>
				<div
					style={{
						display: "flex",
						alignItems: "center",
						justifyContent: "space-between",
						marginBottom: 14,
					}}
				>
					<h2
						style={{
							...display,
							fontSize: 20,
							fontWeight: 500,
							color: "var(--color-dashboard-text)",
							margin: 0,
						}}
					>
						Sections
					</h2>
					{editable && sections.length > 0 && (
						<StudioButton
							variant="secondary"
							size="sm"
							icon={Plus}
							onClick={() => setShowAddSection(true)}
						>
							Add Section
						</StudioButton>
					)}
				</div>

				{sections.length === 0 ? (
					<FloatPanel elevation={1} padding={48}>
						<div style={{ textAlign: "center" }}>
							<FileText
								size={36}
								style={{
									color: "var(--color-dashboard-faint-text)",
									margin: "0 auto 14px",
									display: "block",
								}}
							/>
							<p
								style={{
									...display,
									fontSize: 17,
									color: "var(--color-dashboard-muted-dark)",
									marginBottom: 6,
								}}
							>
								No sections yet
							</p>
							<p
								style={{
									...body,
									fontSize: 13,
									color: "var(--color-dashboard-placeholder)",
									marginBottom: editable ? 16 : 0,
								}}
							>
								Add a section to start organising your BOQ entries
							</p>
							{editable && (
								<StudioButton
									variant="secondary"
									size="sm"
									icon={Plus}
									onClick={() => setShowAddSection(true)}
								>
									Add First Section
								</StudioButton>
							)}
						</div>
					</FloatPanel>
				) : (
					<div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
						{sections.map((section) => (
							<FloatPanel key={section.id} elevation={1} padding={0}>
								{/* Section header */}
								<div
									style={{
										display: "flex",
										alignItems: "flex-start",
										justifyContent: "space-between",
										padding: "18px 22px",
										borderBottom:
											"1px solid var(--color-dashboard-border-soft)",
										gap: 12,
									}}
								>
									<div style={{ flex: 1, minWidth: 0 }}>
										<div
											style={{
												display: "flex",
												alignItems: "center",
												gap: 8,
												flexWrap: "wrap",
											}}
										>
											<h3
												style={{
													...display,
													fontSize: 17,
													fontWeight: 500,
													color: "var(--color-dashboard-text)",
													margin: 0,
												}}
											>
												{section.sectionName}
											</h3>
											{section.category && (
												<span
													style={{
														...mono,
														fontSize: 9,
														letterSpacing: "0.08em",
														textTransform: "uppercase",
														background: "var(--color-dashboard-divider)",
														color: "var(--color-dashboard-accent-dark)",
														border: "1px solid var(--color-dashboard-border)",
														borderRadius: 14,
														padding: "2px 9px",
													}}
												>
													{section.category}
												</span>
											)}
											{section.isDefault && (
												<span
													style={{
														...mono,
														fontSize: 9,
														letterSpacing: "0.08em",
														textTransform: "uppercase",
														background: "var(--color-dashboard-surface)",
														color: "var(--color-dashboard-muted)",
														border: "1px solid var(--color-dashboard-border)",
														borderRadius: 14,
														padding: "2px 9px",
													}}
												>
													Default
												</span>
											)}
										</div>
										{section.description && (
											<p
												style={{
													...body,
													fontSize: 12.5,
													color: "var(--color-dashboard-muted)",
													marginTop: 4,
												}}
											>
												{section.description}
											</p>
										)}
									</div>

									<div
										style={{
											display: "flex",
											alignItems: "center",
											gap: 12,
											flexShrink: 0,
										}}
									>
										<div style={{ textAlign: "right" }}>
											<p
												style={{
													...mono,
													fontSize: 9,
													letterSpacing: "0.08em",
													textTransform: "uppercase",
													color: "var(--color-dashboard-placeholder)",
													marginBottom: 2,
												}}
											>
												Section Total
											</p>
											<p
												style={{
													...display,
													fontSize: 18,
													fontWeight: 600,
													color: "var(--color-dashboard-text)",
												}}
											>
												{formatCurrency(section.sectionTotal, boq.currencyCode)}
											</p>
										</div>
										{editable && (
											<DropdownMenu>
												<DropdownMenuTrigger asChild>
													<button
														type="button"
														style={{
															display: "flex",
															alignItems: "center",
															justifyContent: "center",
															width: 32,
															height: 32,
															borderRadius: 6,
															background: "transparent",
															border: "1px solid var(--color-dashboard-border)",
															cursor: "pointer",
															color: "var(--color-dashboard-muted)",
														}}
													>
														<MoreVertical size={15} />
													</button>
												</DropdownMenuTrigger>
												<DropdownMenuContent
													align="end"
													style={{
														background: "var(--color-dashboard-paper)",
														border: "1px solid var(--color-dashboard-border)",
														borderRadius: 8,
														boxShadow:
															"0 4px 12px color-mix(in srgb, var(--color-dashboard-shadow-ink) 8%, transparent)",
														padding: 6,
														minWidth: 160,
													}}
												>
													<DropdownMenuItem
														onClick={() => setEditSection(section)}
														className="hover:bg-[var(--color-dashboard-divider)] focus:bg-[var(--color-dashboard-divider)] focus:text-[var(--color-dashboard-text)] cursor-pointer"
														style={{
															display: "flex",
															alignItems: "center",
															padding: "8px 12px",
															borderRadius: 6,
															fontSize: 13.5,
															fontFamily: "var(--font-body)",
															color: "var(--color-dashboard-text)",
															transition: "background 150ms",
														}}
													>
														<Edit2 className="mr-2 h-4 w-4 text-[var(--color-dashboard-muted)]" />
														Edit Section
													</DropdownMenuItem>
													{!section.isDefault && (
														<>
															<DropdownMenuSeparator
																style={{
																	background: "var(--color-dashboard-divider)",
																	margin: "4px 0",
																}}
															/>
															<DropdownMenuItem
																className="text-dashboard-pill-risk hover:bg-dashboard-danger-bg focus:bg-dashboard-danger-bg focus:text-dashboard-pill-risk cursor-pointer"
																onClick={() => setDeleteSection(section)}
																style={{
																	display: "flex",
																	alignItems: "center",
																	padding: "8px 12px",
																	borderRadius: 6,
																	fontSize: 13.5,
																	fontFamily: "var(--font-body)",
																	transition: "background 150ms",
																}}
															>
																<Trash2 className="mr-2 h-4 w-4" />
																Delete Section
															</DropdownMenuItem>
														</>
													)}
												</DropdownMenuContent>
											</DropdownMenu>
										)}
									</div>
								</div>

								{/* Entries table */}
								{section.entries && section.entries.length > 0 ? (
									<div style={{ overflowX: "auto" }}>
										<table
											style={{ width: "100%", borderCollapse: "collapse" }}
										>
											<thead>
												<tr
													style={{
														background: "var(--color-dashboard-paper)",
														borderBottom:
															"1px solid var(--color-dashboard-border-soft)",
													}}
												>
													{[
														{ label: "#", align: "left", w: 40 },
														{ label: "Product", align: "left" },
														{ label: "Description", align: "left" },
														{ label: "Room", align: "left", w: 100 },
														{ label: "UoM", align: "right", w: 70 },
														{ label: "Qty", align: "right", w: 70 },
														{ label: "Unit Price", align: "right", w: 110 },
														{ label: "Line Total", align: "right", w: 120 },
														{ label: "Approved", align: "center", w: 90 },
														...(editable
															? [{ label: "", align: "right", w: 72 }]
															: []),
													].map((col) => (
														<th
															key={col.label}
															style={{
																...mono,
																fontSize: 9,
																letterSpacing: "0.08em",
																textTransform: "uppercase",
																color: "var(--color-dashboard-placeholder)",
																fontWeight: 600,
																textAlign: col.align as "left" | "right",
																padding: "10px 14px",
																whiteSpace: "nowrap",
																width: col.w,
															}}
														>
															{col.label}
														</th>
													))}
												</tr>
											</thead>
											<tbody>
												{section.entries.map((entry, idx) => (
													<tr
														key={entry.id}
														style={{
															borderBottom:
																idx < section.entries.length - 1
																	? "1px solid var(--color-dashboard-surface)"
																	: "none",
														}}
														className="group"
													>
														<td
															style={{
																...mono,
																fontSize: 11,
																color: "var(--color-dashboard-placeholder)",
																padding: "12px 14px",
															}}
														>
															{entry.lineNo}
														</td>
														<td
															style={{
																...body,
																fontSize: 13.5,
																fontWeight: 500,
																color: "var(--color-dashboard-text)",
																padding: "12px 14px",
															}}
														>
															{entry.product?.name || entry.catalogProductId}
														</td>
														<td
															style={{
																...body,
																fontSize: 13,
																color: "var(--color-dashboard-muted)",
																padding: "12px 14px",
															}}
														>
															{entry.description || "—"}
														</td>
														<td
															style={{
																...body,
																fontSize: 12,
																color: "var(--color-dashboard-muted-dark)",
																padding: "12px 14px",
															}}
														>
															{entry.roomCustomName || entry.roomName || "—"}
														</td>
														<td
															style={{
																...mono,
																fontSize: 11,
																color: "var(--color-dashboard-muted-dark)",
																padding: "12px 14px",
																textAlign: "right",
															}}
														>
															{entry.uom || "—"}
														</td>
														<td
															style={{
																...mono,
																fontSize: 12,
																color: "var(--color-dashboard-muted-dark)",
																padding: "12px 14px",
																textAlign: "right",
															}}
														>
															{entry.quantity}
														</td>
														<td
															style={{
																...body,
																fontSize: 13,
																color: "var(--color-dashboard-muted-dark)",
																padding: "12px 14px",
																textAlign: "right",
															}}
														>
															{formatCurrency(
																entry.unitPrice,
																boq.currencyCode,
															)}
														</td>
														<td
															style={{
																...display,
																fontSize: 14,
																fontWeight: 600,
																color: "var(--color-dashboard-text)",
																padding: "12px 14px",
																textAlign: "right",
															}}
														>
															{formatCurrency(
																entry.lineTotal,
																boq.currencyCode,
															)}
														</td>
														<td
															style={{
																padding: "12px 14px",
																textAlign: "center",
															}}
														>
															{entry.approvedByHomeowner ? (
																<span
																	style={{
																		display: "inline-flex",
																		alignItems: "center",
																		justifyContent: "center",
																		padding: "3px 8px",
																		borderRadius: 14,
																		fontSize: 9,
																		fontFamily: "var(--font-mono)",
																		fontWeight: 600,
																		letterSpacing: "0.08em",
																		textTransform: "uppercase",
																		background:
																			"var(--color-dashboard-success-bg)",
																		color: "var(--color-dashboard-success)",
																		border:
																			"1px solid var(--color-dashboard-success-border)",
																	}}
																>
																	Homeowner
																</span>
															) : (
																<span
																	style={{
																		color: "var(--color-dashboard-faint-text)",
																		fontSize: 16,
																	}}
																>
																	—
																</span>
															)}
														</td>
														{editable && (
															<td
																style={{
																	padding: "12px 14px",
																	textAlign: "right",
																}}
															>
																<div
																	style={{
																		display: "flex",
																		justifyContent: "flex-end",
																		gap: 4,
																		transition: "opacity 150ms",
																	}}
																	className="opacity-0 group-hover:opacity-100"
																>
																	<button
																		type="button"
																		onClick={() => setEditEntry(entry)}
																		title="Edit entry"
																		style={{
																			display: "flex",
																			alignItems: "center",
																			justifyContent: "center",
																			width: 28,
																			height: 28,
																			borderRadius: 5,
																			background: "transparent",
																			border:
																				"1px solid var(--color-dashboard-border)",
																			cursor: "pointer",
																			color: "var(--color-dashboard-muted)",
																		}}
																	>
																		<Edit2 size={12} />
																	</button>
																	<button
																		type="button"
																		onClick={() => setDeleteEntry(entry)}
																		title="Delete entry"
																		style={{
																			display: "flex",
																			alignItems: "center",
																			justifyContent: "center",
																			width: 28,
																			height: 28,
																			borderRadius: 5,
																			background: "transparent",
																			border:
																				"1px solid var(--color-dashboard-danger-border)",
																			cursor: "pointer",
																			color: "#EF4444",
																		}}
																	>
																		<Trash2 size={12} />
																	</button>
																</div>
															</td>
														)}
													</tr>
												))}
											</tbody>
										</table>
									</div>
								) : (
									<div
										style={{
											padding: "28px 22px",
											textAlign: "center",
											borderTop: "1px dashed var(--color-dashboard-border)",
										}}
									>
										<p
											style={{
												...body,
												fontSize: 13,
												color: "var(--color-dashboard-placeholder)",
												fontStyle: "italic",
											}}
										>
											No entries in this section.{" "}
											{editable && (
												<button
													type="button"
													onClick={() => {
														setSelectedSectionId(section.id);
														setShowAddEntry(true);
													}}
													style={{
														...body,
														fontSize: 13,
														color: "var(--color-dashboard-accent-dark)",
														fontWeight: 500,
														textDecoration: "underline",
														background: "none",
														border: "none",
														cursor: "pointer",
														padding: 0,
													}}
												>
													Add first entry
												</button>
											)}
										</p>
									</div>
								)}
								{editable && section.entries && section.entries.length > 0 && (
									<div
										style={{
											padding: "12px 22px",
											borderTop: "1px solid var(--color-dashboard-border-soft)",
										}}
									>
										<StudioButton
											variant="secondary"
											size="sm"
											icon={Plus}
											onClick={() => {
												setSelectedSectionId(section.id);
												setShowAddEntry(true);
											}}
										>
											Add Entry
										</StudioButton>
									</div>
								)}
							</FloatPanel>
						))}
					</div>
				)}
			</div>

			{/* ── Grand Total ────────────────────────────────────────────────── */}
			<FloatPanel elevation={2} padding={22}>
				<div
					style={{
						display: "flex",
						alignItems: "center",
						justifyContent: "space-between",
					}}
				>
					<div>
						<p
							style={{
								...mono,
								fontSize: 9,
								letterSpacing: "0.1em",
								textTransform: "uppercase",
								color: "var(--color-dashboard-muted)",
								marginBottom: 4,
							}}
						>
							Grand Total
						</p>
						<p
							style={{
								...display,
								fontSize: 28,
								fontWeight: 600,
								color: "var(--color-dashboard-accent-dark)",
								letterSpacing: "-0.02em",
							}}
						>
							{formatCurrency(boq.grandTotal, boq.currencyCode)}
						</p>
					</div>
					<div
						style={{
							width: 48,
							height: 48,
							borderRadius: 10,
							background:
								"linear-gradient(135deg, var(--color-dashboard-accent), #FB923C)",
							display: "flex",
							alignItems: "center",
							justifyContent: "center",
						}}
					>
						<FileText
							size={22}
							style={{ color: "var(--color-dashboard-on-fill)" }}
						/>
					</div>
				</div>
			</FloatPanel>

			{/* ── Change Requests ────────────────────────────────────────────── */}
			<FloatPanel elevation={1} padding={22}>
				<div
					style={{
						display: "flex",
						alignItems: "center",
						gap: 10,
						marginBottom: 16,
					}}
				>
					<MessageSquare
						size={16}
						style={{ color: "var(--color-dashboard-accent-dark)" }}
					/>
					<h2
						style={{
							...display,
							fontSize: 18,
							fontWeight: 500,
							color: "var(--color-dashboard-text)",
							margin: 0,
						}}
					>
						Change Requests
					</h2>
				</div>
				<ChangeRequestsList
					boq={boq}
					refreshTrigger={changeRequestsRefresh}
					canResolve={isProvider}
					onResolved={loadBOQ}
				/>
			</FloatPanel>

			{/* ── Dialogs ────────────────────────────────────────────────────── */}
			<AddSectionDialog
				boqId={boq.id}
				open={showAddSection}
				onOpenChange={setShowAddSection}
				onSuccess={loadBOQ}
			/>

			{showAddEntry && (
				<AddEntryDialog
					boqId={boq.id}
					sections={sections}
					open={showAddEntry}
					onOpenChange={setShowAddEntry}
					onSuccess={loadBOQ}
					defaultSectionId={selectedSectionId}
				/>
			)}

			<EditSectionDialog
				boqId={boq.id}
				section={editSection}
				open={!!editSection}
				onOpenChange={(open) => !open && setEditSection(null)}
				onSuccess={loadBOQ}
			/>

			<EditEntryDialog
				boqId={boq.id}
				entry={editEntry}
				sections={sections}
				open={!!editEntry}
				onOpenChange={(open) => !open && setEditEntry(null)}
				onSuccess={loadBOQ}
			/>

			<RequestChangesDialog
				boq={boq}
				open={showRequestChanges}
				onOpenChange={setShowRequestChanges}
				onSuccess={handleChangeRequestSuccess}
			/>

			{/* Delete Section */}
			{deleteSection && (
				<div
					role="dialog"
					aria-modal="true"
					style={{
						position: "fixed",
						inset: 0,
						zIndex: 200,
						display: "flex",
						alignItems: "center",
						justifyContent: "center",
						padding: "20px 16px",
						background: "rgba(30, 41, 59, 0.45)",
						backdropFilter: "blur(4px)",
					}}
					onClick={(e) => {
						if (e.target === e.currentTarget) setDeleteSection(null);
					}}
					onKeyDown={(e) => {
						if (e.key === "Escape") setDeleteSection(null);
					}}
				>
					<FloatPanel
						elevation={4}
						padding={0}
						style={{ width: "100%", maxWidth: 420, overflow: "hidden" }}
					>
						<div style={{ padding: "24px 24px 20px" }}>
							<h3
								style={{
									fontFamily: "var(--font-display)",
									fontStyle: "italic",
									fontSize: 18,
									fontWeight: 500,
									color: "var(--color-dashboard-text)",
									margin: "0 0 10px 0",
								}}
							>
								Delete Section
							</h3>
							<p
								style={{
									fontFamily: "var(--font-body)",
									fontSize: 14,
									color: "var(--color-dashboard-muted-dark)",
									lineHeight: 1.5,
									margin: 0,
								}}
							>
								Are you sure you want to delete "{deleteSection.sectionName}"?
								This will also delete all entries in this section. This action
								cannot be undone.
							</p>
						</div>

						<div
							style={{
								display: "flex",
								justifyContent: "flex-end",
								gap: 10,
								padding: "16px 24px",
								borderTop: "1px solid var(--color-dashboard-border)",
								background: "var(--color-dashboard-surface)",
							}}
						>
							<StudioButton
								variant="secondary"
								size="sm"
								type="button"
								onClick={() => setDeleteSection(null)}
								disabled={isDeleting}
							>
								Cancel
							</StudioButton>
							<button
								type="button"
								onClick={handleDeleteSection}
								disabled={isDeleting}
								style={{
									display: "inline-flex",
									alignItems: "center",
									justifyContent: "center",
									height: 36,
									paddingLeft: 14,
									paddingRight: 14,
									fontSize: 13,
									fontFamily: "var(--font-body)",
									fontWeight: 500,
									borderRadius: 8,
									border: "none",
									background: "var(--color-dashboard-danger-fill)",
									color: "var(--color-dashboard-on-fill)",
									cursor: isDeleting ? "not-allowed" : "pointer",
									opacity: isDeleting ? 0.6 : 1,
									transition: "background 150ms",
								}}
								onMouseEnter={(e) => {
									if (!isDeleting)
										e.currentTarget.style.background =
											"var(--color-dashboard-danger)";
								}}
								onMouseLeave={(e) => {
									if (!isDeleting)
										e.currentTarget.style.background =
											"var(--color-dashboard-pill-risk)";
								}}
							>
								{isDeleting ? (
									<>
										<Loader2
											size={14}
											style={{
												marginRight: 6,
												animation: "spin 1s linear infinite",
											}}
										/>
										Deleting…
									</>
								) : (
									"Delete Section"
								)}
							</button>
						</div>
					</FloatPanel>
				</div>
			)}

			{/* Delete Entry */}
			{deleteEntry && (
				<div
					role="dialog"
					aria-modal="true"
					style={{
						position: "fixed",
						inset: 0,
						zIndex: 200,
						display: "flex",
						alignItems: "center",
						justifyContent: "center",
						padding: "20px 16px",
						background: "rgba(30, 41, 59, 0.45)",
						backdropFilter: "blur(4px)",
					}}
					onClick={(e) => {
						if (e.target === e.currentTarget) setDeleteEntry(null);
					}}
					onKeyDown={(e) => {
						if (e.key === "Escape") setDeleteEntry(null);
					}}
				>
					<FloatPanel
						elevation={4}
						padding={0}
						style={{ width: "100%", maxWidth: 420, overflow: "hidden" }}
					>
						<div style={{ padding: "24px 24px 20px" }}>
							<h3
								style={{
									fontFamily: "var(--font-display)",
									fontStyle: "italic",
									fontSize: 18,
									fontWeight: 500,
									color: "var(--color-dashboard-text)",
									margin: "0 0 10px 0",
								}}
							>
								Delete Entry
							</h3>
							<p
								style={{
									fontFamily: "var(--font-body)",
									fontSize: 14,
									color: "var(--color-dashboard-muted-dark)",
									lineHeight: 1.5,
									margin: 0,
								}}
							>
								Are you sure you want to delete this entry?
								{deleteEntry.product && (
									<span
										style={{
											display: "block",
											marginTop: 8,
											fontWeight: 600,
											color: "var(--color-dashboard-text)",
										}}
									>
										Product: {deleteEntry.product.name}
									</span>
								)}
								This action cannot be undone.
							</p>
						</div>

						<div
							style={{
								display: "flex",
								justifyContent: "flex-end",
								gap: 10,
								padding: "16px 24px",
								borderTop: "1px solid var(--color-dashboard-border)",
								background: "var(--color-dashboard-surface)",
							}}
						>
							<StudioButton
								variant="secondary"
								size="sm"
								type="button"
								onClick={() => setDeleteEntry(null)}
								disabled={isDeleting}
							>
								Cancel
							</StudioButton>
							<button
								type="button"
								onClick={handleDeleteEntry}
								disabled={isDeleting}
								style={{
									display: "inline-flex",
									alignItems: "center",
									justifyContent: "center",
									height: 36,
									paddingLeft: 14,
									paddingRight: 14,
									fontSize: 13,
									fontFamily: "var(--font-body)",
									fontWeight: 500,
									borderRadius: 8,
									border: "none",
									background: "var(--color-dashboard-danger-fill)",
									color: "var(--color-dashboard-on-fill)",
									cursor: isDeleting ? "not-allowed" : "pointer",
									opacity: isDeleting ? 0.6 : 1,
									transition: "background 150ms",
								}}
								onMouseEnter={(e) => {
									if (!isDeleting)
										e.currentTarget.style.background =
											"var(--color-dashboard-danger)";
								}}
								onMouseLeave={(e) => {
									if (!isDeleting)
										e.currentTarget.style.background =
											"var(--color-dashboard-pill-risk)";
								}}
							>
								{isDeleting ? (
									<>
										<Loader2
											size={14}
											style={{
												marginRight: 6,
												animation: "spin 1s linear infinite",
											}}
										/>
										Deleting…
									</>
								) : (
									"Delete Entry"
								)}
							</button>
						</div>
					</FloatPanel>
				</div>
			)}

			{/* Lock BOQ Confirmation. Locking is final: the app has no unlock. */}
			{lockConfirm && (
				<div
					ref={lockDialogRef}
					role="dialog"
					aria-modal="true"
					aria-labelledby="lock-boq-title"
					style={{
						position: "fixed",
						inset: 0,
						zIndex: 200,
						display: "flex",
						alignItems: "center",
						justifyContent: "center",
						padding: "20px 16px",
						background:
							"color-mix(in srgb, var(--color-dashboard-shadow-ink) 45%, transparent)",
						backdropFilter: "blur(4px)",
					}}
					onClick={(e) => {
						if (e.target === e.currentTarget) setLockConfirm(false);
					}}
					onKeyDown={(e) => {
						if (e.key === "Escape") setLockConfirm(false);
					}}
				>
					<FloatPanel
						elevation={4}
						padding={0}
						style={{ width: "100%", maxWidth: 420, overflow: "hidden" }}
					>
						<div style={{ padding: "24px 24px 20px" }}>
							<h3
								id="lock-boq-title"
								style={{
									fontFamily: "var(--font-display)",
									fontStyle: "italic",
									fontSize: 18,
									fontWeight: 500,
									color: "var(--color-dashboard-text)",
									margin: "0 0 10px 0",
								}}
							>
								Lock this BOQ?
							</h3>
							<p
								style={{
									fontFamily: "var(--font-body)",
									fontSize: 14,
									color: "var(--color-dashboard-muted-dark)",
									lineHeight: 1.5,
									margin: 0,
								}}
							>
								Once it is locked, nobody can edit it, approve its lines or
								request changes to it. It cannot be unlocked.
							</p>
						</div>
						<div
							style={{
								display: "flex",
								justifyContent: "flex-end",
								gap: 10,
								padding: "16px 24px",
								borderTop: "1px solid var(--color-dashboard-border)",
								background: "var(--color-dashboard-surface)",
							}}
						>
							<StudioButton
								variant="secondary"
								size="sm"
								type="button"
								onClick={() => setLockConfirm(false)}
								disabled={actionLoading === "lock"}
							>
								Cancel
							</StudioButton>
							<StudioButton
								variant="primary"
								size="sm"
								type="button"
								icon={Lock}
								onClick={async () => {
									await handleLock();
									setLockConfirm(false);
								}}
								disabled={actionLoading === "lock"}
							>
								{actionLoading === "lock" ? "Locking…" : "Lock BOQ"}
							</StudioButton>
						</div>
					</FloatPanel>
				</div>
			)}

			{/* Delete BOQ Confirmation */}
			{deleteBoqConfirm && (
				<div
					role="dialog"
					aria-modal="true"
					style={{
						position: "fixed",
						inset: 0,
						zIndex: 200,
						display: "flex",
						alignItems: "center",
						justifyContent: "center",
						padding: "20px 16px",
						background: "rgba(30, 41, 59, 0.45)",
						backdropFilter: "blur(4px)",
					}}
					onClick={(e) => {
						if (e.target === e.currentTarget) setDeleteBoqConfirm(false);
					}}
					onKeyDown={(e) => {
						if (e.key === "Escape") setDeleteBoqConfirm(false);
					}}
				>
					<FloatPanel
						elevation={4}
						padding={0}
						style={{ width: "100%", maxWidth: 420, overflow: "hidden" }}
					>
						<div style={{ padding: "24px 24px 20px" }}>
							<h3
								style={{
									fontFamily: "var(--font-display)",
									fontStyle: "italic",
									fontSize: 18,
									fontWeight: 500,
									color: "var(--color-dashboard-text)",
									margin: "0 0 10px 0",
								}}
							>
								Delete BOQ
							</h3>
							<p
								style={{
									fontFamily: "var(--font-body)",
									fontSize: 14,
									color: "var(--color-dashboard-muted-dark)",
									lineHeight: 1.5,
									margin: 0,
								}}
							>
								Are you sure you want to delete this BOQ? This will permanently
								remove all sections and entries. This action cannot be undone.
							</p>
						</div>

						<div
							style={{
								display: "flex",
								justifyContent: "flex-end",
								gap: 10,
								padding: "16px 24px",
								borderTop: "1px solid var(--color-dashboard-border)",
								background: "var(--color-dashboard-surface)",
							}}
						>
							<StudioButton
								variant="secondary"
								size="sm"
								type="button"
								onClick={() => setDeleteBoqConfirm(false)}
								disabled={isDeleting}
							>
								Cancel
							</StudioButton>
							<button
								type="button"
								onClick={handleDeleteBoq}
								disabled={isDeleting}
								style={{
									display: "inline-flex",
									alignItems: "center",
									justifyContent: "center",
									height: 36,
									paddingLeft: 14,
									paddingRight: 14,
									fontSize: 13,
									fontFamily: "var(--font-body)",
									fontWeight: 500,
									borderRadius: 8,
									border: "none",
									background: "var(--color-dashboard-danger-fill)",
									color: "var(--color-dashboard-on-fill)",
									cursor: isDeleting ? "not-allowed" : "pointer",
									opacity: isDeleting ? 0.6 : 1,
									transition: "background 150ms",
								}}
								onMouseEnter={(e) => {
									if (!isDeleting)
										e.currentTarget.style.background =
											"var(--color-dashboard-danger)";
								}}
								onMouseLeave={(e) => {
									if (!isDeleting)
										e.currentTarget.style.background =
											"var(--color-dashboard-pill-risk)";
								}}
							>
								{isDeleting ? (
									<>
										<Loader2
											size={14}
											style={{
												marginRight: 6,
												animation: "spin 1s linear infinite",
											}}
										/>
										Deleting…
									</>
								) : (
									"Delete BOQ"
								)}
							</button>
						</div>
					</FloatPanel>
				</div>
			)}
		</div>
	);
}
